import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchStatuses } from '../../../../constants/fetchStatuses';
import {
  FetchCsvTemplatesResponse,
  FetchCsvTemplateResponse,
  FetchCsvTemplateNameOptionsResponse,
  CreateCsvTemplateResponse,
  UpdateCsvTemplateResponse,
  DeleteCsvTemplateResponse,
} from '../../../../types/b2tRatibiPlus';
import {
  fetchCsvTemplates,
  fetchCsvTemplate,
  fetchCsvTemplateNameOptions,
  createCsvTemplate,
  updateCsvTemplate,
  deleteCsvTemplate,
} from './csvTempletes.actions';
import { CsvTemplatesState } from './csvTempletes.types';

const initialState: CsvTemplatesState = {
  csvTemplates: [],
  selectedCsvTemplate: null,
  totalCsvTemplates: 0,
  csvTemplateNameOptions: [],
  fetchCsvTemplatesStatus: fetchStatuses.idle,
  fetchCsvTemplateStatus: fetchStatuses.idle,
  fetchCsvTemplateNameOptionsStatus: fetchStatuses.idle,
  createCsvTemplateStatus: fetchStatuses.idle,
  updateCsvTemplateStatus: fetchStatuses.idle,
  deleteCsvTemplateStatus: fetchStatuses.idle,
  error: null,
};

const csvTemplatesSlice = createSlice({
  name: 'csvTemplates',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // === FETCH CSV TEMPLATES ===
    builder.addCase(fetchCsvTemplates.pending, (state) => {
      state.error = null;
      state.fetchCsvTemplatesStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchCsvTemplates.fulfilled, (state, action: PayloadAction<FetchCsvTemplatesResponse>) => {
      state.fetchCsvTemplatesStatus = fetchStatuses.success;
      state.csvTemplates = action.payload.data?.templates ?? [];
      state.totalCsvTemplates = action.payload.data?.total ?? 0;
    });
    builder.addCase(fetchCsvTemplates.rejected, (state, action) => {
      state.fetchCsvTemplatesStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === FETCH CSV TEMPLATE ===
    builder.addCase(fetchCsvTemplate.pending, (state) => {
      state.fetchCsvTemplateStatus = fetchStatuses.pending;
      state.error = null;
    });
    builder.addCase(fetchCsvTemplate.fulfilled, (state, action: PayloadAction<FetchCsvTemplateResponse>) => {
      state.fetchCsvTemplateStatus = fetchStatuses.success;
      state.selectedCsvTemplate = action.payload.data ?? null;
    });
    builder.addCase(fetchCsvTemplate.rejected, (state, action) => {
      state.fetchCsvTemplateStatus = fetchStatuses.rejected;
      state.selectedCsvTemplate = null;
      state.error = action.error;
    });

    // === FETCH CSV TEMPLATE NAME OPTIONS ===
    builder.addCase(fetchCsvTemplateNameOptions.pending, (state) => {
      state.fetchCsvTemplateNameOptionsStatus = fetchStatuses.pending;
      state.error = null;
    });
    builder.addCase(
      fetchCsvTemplateNameOptions.fulfilled,
      (state, action: PayloadAction<FetchCsvTemplateNameOptionsResponse>) => {
        state.fetchCsvTemplateNameOptionsStatus = fetchStatuses.success;
        state.csvTemplateNameOptions = action.payload.data?.options ?? [];
      },
    );
    builder.addCase(fetchCsvTemplateNameOptions.rejected, (state, action) => {
      state.fetchCsvTemplateNameOptionsStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === CREATE CSV TEMPLATE ===
    builder.addCase(createCsvTemplate.pending, (state) => {
      state.createCsvTemplateStatus = fetchStatuses.pending;
      state.error = null;
    });
    builder.addCase(createCsvTemplate.fulfilled, (state, action: PayloadAction<CreateCsvTemplateResponse>) => {
      state.createCsvTemplateStatus = fetchStatuses.success;

      if (action.payload.data) {
        state.csvTemplates = [action.payload.data, ...state.csvTemplates];
        state.totalCsvTemplates += 1;
      }
    });
    builder.addCase(createCsvTemplate.rejected, (state, action) => {
      state.createCsvTemplateStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === UPDATE CSV TEMPLATE ===
    builder.addCase(updateCsvTemplate.pending, (state) => {
      state.updateCsvTemplateStatus = fetchStatuses.pending;
      state.error = null;
    });
    builder.addCase(updateCsvTemplate.fulfilled, (state, action: PayloadAction<UpdateCsvTemplateResponse>) => {
      state.updateCsvTemplateStatus = fetchStatuses.success;
      const updatedId = action.payload.data?.id;
      if (updatedId) {
        state.csvTemplates = state.csvTemplates.map((template) => {
          if (template.id === updatedId) {
            return { ...template, updatedAt: new Date().toISOString() };
          }

          return template;
        });
      }
    });
    builder.addCase(updateCsvTemplate.rejected, (state, action) => {
      state.updateCsvTemplateStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === DELETE CSV TEMPLATE ===
    builder.addCase(deleteCsvTemplate.pending, (state) => {
      state.deleteCsvTemplateStatus = fetchStatuses.pending;
      state.error = null;
    });
    builder.addCase(deleteCsvTemplate.fulfilled, (state, action: PayloadAction<DeleteCsvTemplateResponse>) => {
      state.deleteCsvTemplateStatus = fetchStatuses.success;
      const deletedId = action.payload.data?.id;
      if (deletedId) {
        state.csvTemplates = state.csvTemplates.filter((template) => template.id !== deletedId);
        state.totalCsvTemplates = Math.max(0, state.totalCsvTemplates - 1);
      }
    });
    builder.addCase(deleteCsvTemplate.rejected, (state, action) => {
      state.deleteCsvTemplateStatus = fetchStatuses.rejected;
      state.error = action.error;
    });
  },
});

export default csvTemplatesSlice.reducer;
